<template>
  <v-content class="bg-content-box">
    <v-container fluid>
      <v-row justify="center">
        <v-col cols="12" md="10">
          <v-card class="glass-card pa-6 my-8" outlined>
            <h2 class="mb-4" style="text-align:center;">
              <v-icon large class="mr-2">mdi-cube-outline</v-icon>
              Shadowbox Project Viewer
            </h2>
            <div v-if="!loaded && !error">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <div class="grey--text text--darken-1 mt-2">Loading...</div>
            </div>
            <div v-if="error" class="error--text pa-8" style="text-align:center;">
              <v-icon large color="error" class="mb-3">mdi-alert</v-icon>
              <br>
              Project not found or link is invalid.
            </div>
            <div v-if="loaded && !error">
              <div class="mb-4" style="text-align:center;">
                <v-btn-toggle v-model="mode" mandatory>
                  <v-btn value="flat"><v-icon left>mdi-image</v-icon>2D Flat</v-btn>
                  <v-btn value="3d"><v-icon left>mdi-cube-outline</v-icon>3D Render</v-btn>
                </v-btn-toggle>
              </div>
              <div v-show="mode==='flat'" style="display:flex;justify-content:center;">
                <canvas ref="canvasFlat" width="600" height="450"
                  style="border-radius:13px;border:2px solid #404360;box-shadow:0 2px 28px #0007;display:block;margin:auto;"/>
                <div class="plaque-preview" v-show="project.plaqueText">{{ project.plaqueText }}</div>
              </div>
              <div v-show="mode==='3d'" ref="renderer3d" style="width:100%;min-height:640px;"></div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-content>
</template>

<script>
import * as THREE from "three";
export default {
  name: "ShadowboxViewer",
  data() {
    return {
      mode: "3d",
      loaded: false,
      error: false,
      project: {
        boxWidth: 600,
        boxHeight: 450,
        boxDepth: 40,
        plaqueText: "",
        wallColor: "#d4d6e0",
        wallImage: null,
        background: null,
        layers: [],
        plaque: null,
      }
    };
  },
  watch: {
    mode(val) {
      this.$nextTick(() => {
        if (val === "3d") this.render3DShadowbox();
        else this.drawFlat();
      });
    }
  },
  mounted() {
    // Load data from hash or ?data=
    let encoded = window.location.hash.replace(/^#/, "");
    if (!encoded) {
      const params = new URLSearchParams(window.location.search);
      encoded = params.get("data");
    }
    if (encoded) {
      try {
        const data = JSON.parse(atob(decodeURIComponent(encoded)));
        this.project = this.importCurrentData(data);
        this.loaded = true;
        this.error = false;
        this.$nextTick(() => {
          if (this.mode === "3d") this.render3DShadowbox();
          else this.drawFlat();
        });
      } catch (err) {
        this.error = true;
      }
    } else {
      this.error = true;
    }
  },
  methods: {
    dataToImg(obj) {
      if (!obj || !obj.src) return null;
      const img = new Image();
      img.src = obj.src;
      return { img, src: obj.src };
    },
    importCurrentData(data) {
      let out = {
        boxWidth: data.boxWidth || 600,
        boxHeight: data.boxHeight || 450,
        boxDepth: data.boxDepth || 40,
        plaqueText: data.plaqueText || "",
        wallColor: data.wallColor || "#d4d6e0",
        wallImage: this.dataToImg(data.wallImage),
        background: this.dataToImg(data.background),
        layers: (data.layers || []).map(l => this.dataToImg(l)),
        plaque: this.dataToImg(data.plaque),
      };
      return out;
    },
    drawFlat() {
      const { background, layers, plaque, plaqueText, wallImage } = this.project;
      const canvas = this.$refs.canvasFlat;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      canvas.width = 600;
      canvas.height = 450;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (wallImage) ctx.drawImage(wallImage.img, 0, 0, canvas.width, canvas.height);
      if (background) ctx.drawImage(background.img, 0, 0, canvas.width, canvas.height);
      (layers || []).forEach(layer => {
        ctx.drawImage(layer.img, 0, 0, canvas.width, canvas.height);
      });
      if (plaque) ctx.drawImage(plaque.img, canvas.width / 2 - 80, canvas.height - 66, 160, 38);
      if (plaqueText) {
        ctx.save();
        ctx.font = "bold 19px Segoe UI,Arial";
        ctx.textAlign = "center";
        ctx.fillStyle = "#c4ae76";
        ctx.strokeStyle = "#312916";
        ctx.lineWidth = 2.5;
        ctx.strokeText(plaqueText, canvas.width / 2, canvas.height - 25);
        ctx.fillText(plaqueText, canvas.width / 2, canvas.height - 25);
        ctx.restore();
      }
      ctx.save();
      ctx.strokeStyle = "rgba(135,220,255,0.6)";
      ctx.lineWidth = 8;
      ctx.strokeRect(6, 6, canvas.width - 12, canvas.height - 12);
      ctx.restore();
    },
    render3DShadowbox() {
      const container = this.$refs.renderer3d;
      if (!container) return;
      container.innerHTML = "";
      // Dimensions
      const project = this.project;
      const canvasPixelWidth = 1200;
      const targetAspect = project.boxWidth / project.boxHeight;
      let width = canvasPixelWidth;
      let height = Math.round(width / targetAspect);
      let threeScene = new THREE.Scene();
      let cameraDistance = Math.max(project.boxWidth, project.boxHeight) * 1.55;
      let threeCamera = new THREE.PerspectiveCamera(35, width / height, 1, 3000);
      threeCamera.position.set(0, 0, cameraDistance);

      let threeRenderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
      threeRenderer.setClearColor(0x161a23, 1);
      threeRenderer.setSize(width, height, false);
      container.appendChild(threeRenderer.domElement);

      // Wall
      const wallMargin = 800;
      let wallWidth = project.boxWidth + wallMargin * 2;
      let wallHeight = project.boxHeight + wallMargin * 2;
      let wallZ = -((project.boxDepth / 2) + 2);
      let wallGeometry = new THREE.PlaneGeometry(wallWidth, wallHeight);
      let wallMaterial;
      if (project.wallImage) {
        let tex = new THREE.Texture(project.wallImage.img);
        tex.needsUpdate = true;
        wallMaterial = new THREE.MeshPhongMaterial({ map: tex, shininess: 18, side: THREE.DoubleSide });
      } else {
        wallMaterial = new THREE.MeshPhongMaterial({ color: parseInt(project.wallColor.replace('#','0x'),16), shininess: 18, side: THREE.DoubleSide });
        let wallCanvas = document.createElement('canvas');
        wallCanvas.width = 64; wallCanvas.height = 64;
        let wallCtx = wallCanvas.getContext('2d');
        wallCtx.fillStyle = project.wallColor;
        wallCtx.fillRect(0,0,64,64);
        for(let i=0;i<250;i++){
          wallCtx.fillStyle = "rgba(155,156,178,0.09)";
          wallCtx.beginPath();
          wallCtx.arc(Math.random()*64,Math.random()*64,Math.random()*12,0,7);
          wallCtx.fill();
        }
        let wallTex = new THREE.Texture(wallCanvas); wallTex.needsUpdate = true;
        wallMaterial.map = wallTex;
      }
      let wall = new THREE.Mesh(wallGeometry, wallMaterial);
      wall.position.set(0, 0, wallZ);
      threeScene.add(wall);

      // Wall Shadow
      let shadowGeo = new THREE.PlaneGeometry(project.boxWidth * 1.08, project.boxHeight * 0.15);
      let shadowMat = new THREE.MeshBasicMaterial({
        map: this.generateShadowTexture(project.boxWidth * 1.04, project.boxHeight * 0.13),
        transparent: true, opacity: 0.49, side: THREE.DoubleSide
      });
      let shadowMesh = new THREE.Mesh(shadowGeo, shadowMat);
      shadowMesh.position.set(0, project.boxHeight/2 - project.boxHeight*0.08, wallZ + 1.5);
      shadowMesh.rotation.x = -Math.PI/2.1;
      threeScene.add(shadowMesh);

      // Lighting
      let spot = new THREE.SpotLight(0xffffff, 1.13, 1800, Math.PI/2.1, 0.48, 1.15);
      spot.position.set(0, 540, 1100);
      spot.castShadow = false;
      threeScene.add(spot);
      let rimLight = new THREE.PointLight(0xbbe6fc, 0.34, 900);
      rimLight.position.set(-320, 380, 400);
      threeScene.add(rimLight);
      let fill = new THREE.DirectionalLight(0xd4eaff, 0.22);
      fill.position.set(-140, -220, 260);
      threeScene.add(fill);
      let amb = new THREE.AmbientLight(0x7b88a5, 0.19);
      threeScene.add(amb);

      let frameMargin = 100;
      let frameGeo = new THREE.BoxGeometry(
        project.boxWidth + frameMargin,
        project.boxHeight + frameMargin,
        project.boxDepth + 100
      );
      let frameMat = new THREE.MeshPhysicalMaterial({
        color: 0xbbeaff, metalness: 0.26, roughness: 0.10,
        thickness: 14.0, transmission: 0.99, transparent: true, opacity: 0.53,
        ior: 1.52, reflectivity: 0.92, clearcoat: 1.0, clearcoatRoughness: 0.03,
        side: THREE.BackSide, attenuationDistance: 190, attenuationColor: 0x98eaff
      });
      let frameMesh = new THREE.Mesh(frameGeo, frameMat);
      threeScene.add(frameMesh);

      // Glass
      let glassGeo = new THREE.PlaneGeometry(project.boxWidth, project.boxHeight);
      let glassMat = new THREE.MeshPhysicalMaterial({
        color: 0xffffff, metalness: 0.6, roughness: 0.08,
        transmission: 1.0, opacity: 0.53, ior: 1.52, thickness: 3,
        transparent: true, reflectivity: 0.98, clearcoat: 1.0, clearcoatRoughness: 0.02,
        side: THREE.FrontSide
      });
      let glassMesh = new THREE.Mesh(glassGeo, glassMat);
      glassMesh.position.z = project.boxDepth/2+2;
      threeScene.add(glassMesh);

      // Reflection overlay
      let reflectGeo = new THREE.PlaneGeometry(project.boxWidth, project.boxHeight);
      let reflectMat = new THREE.MeshBasicMaterial({
        map: this.generateReflectionTexture(project.boxWidth, project.boxHeight),
        transparent: true, opacity: 0.5, side: THREE.FrontSide, depthWrite: false
      });
      let reflectMesh = new THREE.Mesh(reflectGeo, reflectMat);
      reflectMesh.position.z = project.boxDepth/2+2.4;
      threeScene.add(reflectMesh);

      // Content (background, layers, plaque)
      let zBack = -project.boxDepth/2 + 24;
      let LAYER_GAP = 20;
      if (project.background) {
        let tex = new THREE.Texture(project.background.img);
        tex.needsUpdate = true;
        let mat = new THREE.MeshBasicMaterial({
          map: tex, transparent: false, depthWrite: true, depthTest: true
        });
        let plane = new THREE.Mesh(new THREE.PlaneGeometry(project.boxWidth, project.boxHeight), mat);
        plane.position.z = zBack;
        threeScene.add(plane);
      }
      (project.layers || []).forEach((layer, idx) => {
        let tex = new THREE.Texture(layer.img);
        tex.needsUpdate = true;
        let mat = new THREE.MeshBasicMaterial({
          map: tex,
          transparent: true,
          depthWrite: true, depthTest: true
        });
        let plane = new THREE.Mesh(new THREE.PlaneGeometry(project.boxWidth, project.boxHeight), mat);
        plane.position.z = zBack + (idx+1)*LAYER_GAP;
        threeScene.add(plane);
      });
      let plaqueZ = zBack + ((project.layers?.length || 0)+2)*LAYER_GAP;
      if (project.plaque) {
        let tex = new THREE.Texture(project.plaque.img);
        tex.needsUpdate = true;
        let mat = new THREE.MeshBasicMaterial({map: tex, transparent: true});
        let mesh = new THREE.Mesh(new THREE.PlaneGeometry(130, 35), mat);
        mesh.position.set(0, project.boxHeight/2-36, plaqueZ);
        threeScene.add(mesh);
      }
      if (project.plaqueText) {
        let cv = document.createElement('canvas');
        cv.width = 280; cv.height = 54;
        let c = cv.getContext('2d');
        c.fillStyle = "#e7d194"; c.strokeStyle = "#645622"; c.lineWidth = 4;
        c.font = "bold 30px Segoe UI,Arial";
        c.strokeRect(0,0,cv.width,cv.height); c.fillRect(0,0,cv.width,cv.height);
        c.fillStyle = "#37312e"; c.textAlign = "center";
        c.font = "bold 26px Segoe UI,Arial";
        c.fillText(project.plaqueText, cv.width/2, cv.height/2+10);
        let tex = new THREE.Texture(cv);
        tex.needsUpdate = true;
        let mat = new THREE.MeshBasicMaterial({map: tex, transparent: true});
        let mesh = new THREE.Mesh(new THREE.PlaneGeometry(150, 40), mat);
        mesh.position.set(0, project.boxHeight/2-36, plaqueZ+3);
        threeScene.add(mesh);
      }

      // Simple Orbit controls
      let isDragging = false, prevX, prevY, yaw = 0, pitch = 0;
      threeRenderer.domElement.style.cursor = "grab";
      threeRenderer.domElement.addEventListener("mousedown", (e) => {
        isDragging = true; prevX = e.clientX; prevY = e.clientY;
        threeRenderer.domElement.style.cursor = "grabbing";
      });
      window.addEventListener("mouseup", () => {
        isDragging = false; threeRenderer.domElement.style.cursor = "grab";
      });
      window.addEventListener("mousemove", (e) => {
        if (!isDragging) return;
        let dx = e.clientX - prevX, dy = e.clientY - prevY;
        yaw += dx * 0.009; pitch += dy * 0.009;
        prevX = e.clientX; prevY = e.clientY;
      });
      threeRenderer.domElement.addEventListener("wheel", (e) => {
        cameraDistance += e.deltaY * 0.7;
        cameraDistance = Math.max(340, Math.min(cameraDistance, 2400));
      });
      function animate() {
        pitch = Math.max(-0.8, Math.min(0.8, pitch));
        let r = cameraDistance;
        threeCamera.position.x = Math.sin(yaw) * r;
        threeCamera.position.y = Math.sin(pitch) * r;
        threeCamera.position.z = Math.cos(yaw) * Math.cos(pitch) * r;
        threeCamera.lookAt(0, 0, 0);
        threeRenderer.render(threeScene, threeCamera);
        requestAnimationFrame(animate);
      }
      animate();
    },
    generateReflectionTexture(w, h) {
      let c = document.createElement("canvas");
      c.width = w; c.height = h;
      let ctx = c.getContext("2d");
      let grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0.01, "rgba(255,255,255,0.48)");
      grad.addColorStop(0.08, "rgba(200,240,255,0.10)");
      grad.addColorStop(0.22, "rgba(0,0,0,0)");
      grad.addColorStop(0.55, "rgba(255,255,255,0.08)");
      grad.addColorStop(0.8, "rgba(255,255,255,0.08)");
      grad.addColorStop(1, "rgba(200,220,255,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      ctx.beginPath();
      ctx.moveTo(0, h * 0.22); ctx.lineTo(w * 0.6, 0);
      ctx.lineWidth = 15;
      ctx.strokeStyle = "rgba(255,255,255,0.11)";
      ctx.stroke();
      let tex = new THREE.Texture(c);
      tex.needsUpdate = true;
      return tex;
    },
    generateShadowTexture(w, h) {
      let c = document.createElement("canvas");
      c.width = w; c.height = h;
      let ctx = c.getContext("2d");
      let grad = ctx.createRadialGradient(w/2, h*0.45, h*0.13, w/2, h*0.45, w*0.6);
      grad.addColorStop(0, "rgba(0,0,0,0.37)");
      grad.addColorStop(0.85, "rgba(0,0,0,0.07)");
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      let tex = new THREE.Texture(c);
      tex.needsUpdate = true;
      return tex;
    }
  }
};
</script>

<style scoped>
.plaque-preview {
  position: absolute;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  background: #bfa96c;
  color: #222;
  padding: 6px 18px;
  border-radius: 5px;
  font-weight: bold;
  font-size: 17px;
  box-shadow: 0 2px 10px #0004;
  border: 1px solid #c7b589;
  max-width: 80%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
